import React, { useEffect, useState } from 'react';
import { provenanceLabel } from './provenance';
import WorkflowRefLink from './WorkflowRefLink';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';
import { entityCategoryLabel } from './entityCategory';

// Field-level history of one client entity (client_events) — every write,
// manual or workflow (NC registration / CAR amendment), in place. Rendered by
// Settings → Clients & Entities (History modal) and the Client Hub profile;
// `fetchPath` is each surface's own events endpoint.
const FIELD_LABELS = {
  entityName: 'Entity Name', uen: 'UEN', clientGroup: 'Client Group', active: 'Relationship',
  entityCategory: 'Entity Category', inactiveDate: 'Inactive Date', inactiveReason: 'Inactive Reason', name: 'Name',
};

const fmtVal = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Active';
  if (v === false) return 'Inactive';
  if (field === 'entityCategory') return entityCategoryLabel(v);
  if (field === 'inactiveDate') return formatDate(v);
  return String(v);
};

const ClientEntityHistory = ({ fetchPath }) => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(fetchPath);
        if (!cancelled) setEvents(data.data || []);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [fetchPath]);

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!error && events === null && <p className="text-sm text-slate-500">Loading…</p>}
      {events !== null && events.length === 0 && (
        <p className="text-sm text-slate-500">No history yet.</p>
      )}
      {(events || []).map((ev) => (
        <div key={ev.id} className="border border-slate-200 rounded-lg">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 rounded-t-lg">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
              ev.action === 'created' ? 'bg-green-50 text-green-700'
                : ev.action === 'deleted' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-700'
            }`}>{ev.action}</span>
            {/* 來源與促成它的申請單——單號點得過去看那張單。 */}
            <span className="text-xs text-slate-500">
              {provenanceLabel(ev.source) || ev.source}
            </span>
            {ev.workflowRef && (
              <span className="text-xs text-slate-400">· <WorkflowRefLink refNo={ev.workflowRef} /></span>
            )}
            <span className="text-xs text-slate-500">· {ev.actorEmail || 'system'}</span>
            <span className="text-xs text-slate-500 ml-auto whitespace-nowrap">{formatDateTime(ev.createdAt)}</span>
          </div>
          {ev.changes && Object.keys(ev.changes).length > 0 && (
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-4 py-1.5 font-medium w-1/3">Field</th>
                  <th className="px-4 py-1.5 font-medium w-1/3">Before</th>
                  <th className="px-4 py-1.5 font-medium w-1/3">After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(ev.changes).map(([field, diff]) => (
                  <tr key={field}>
                    <td className="px-4 py-1.5 text-slate-700 align-top break-words">{FIELD_LABELS[field] || field}</td>
                    <td className="px-4 py-1.5 text-slate-500 align-top break-words">{fmtVal(field, diff.before)}</td>
                    <td className="px-4 py-1.5 text-slate-900 align-top break-words">{fmtVal(field, diff.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default ClientEntityHistory;
